import {
  FormRadioButton,
  FormTextAreaField,
  FormTextField,
} from 'components/form';
import {
  Button,
  Center,
  Spacer,
  toast,
  TRadioButtonOption,
  Typo,
} from 'components/ui-kit';
import { Form, Formik, FormikProps } from 'formik';
import { useFullScreen } from 'hooks';
import { certificateService } from 'lib';
import { CertificateResponse } from 'lib/models/certificate.response';
import { CertificateProperties } from 'lib/models/certificates.response';
import { useRouter } from 'next/navigation';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { CertificateCard } from './certificate-card/certificate-card.component';
import {
  formatNumericString,
  prepareCertificateData,
} from './certificate-form-helpers';
import { validationSchema } from './certificate-form-validation';
import {
  certificateDeliveryType,
  initialValuesForm,
} from './certificate-form.const';
import styles from './certificate-form.module.scss';
import {
  TCertificateForm,
  TCertificateFormComponentProps,
} from './certificate-form.types';
import { RecipientFields } from './recipient-fields.component';
import { StepCard } from '../step-card/step-card.component';

export const CertificateForm = ({
  certificates,
}: TCertificateFormComponentProps): JSX.Element => {
  const isFullScreen = useFullScreen();
  const recaptchaRef = React.createRef<ReCAPTCHA>();
  const router = useRouter();

  const certificatesOptions: Array<TRadioButtonOption> = certificates.map(
    (certificateData: CertificateProperties) => {
      return {
        value: parseInt(certificateData.price).toString(),
        label: formatNumericString(certificateData.price),
      };
    },
  );

  const handleFormSubmit = (values: TCertificateForm): void => {
    const data = prepareCertificateData(values, certificates);

    certificateService
      .sendCertificate(data)
      .then((response: CertificateResponse) => {
        void router.push(response.data.confirmation.confirmationUrl);
      })
      .catch(() => toast.error('Не удалось отправить сертификат'));
  };

  return (
    <Formik<TCertificateForm>
      initialValues={initialValuesForm}
      onSubmit={handleFormSubmit}
      validationSchema={validationSchema}
      initialTouched={{
        fromEmail: true,
        deliveryMethod: true,
        toEmail: true,
        deliveryDate: true,
      }}
    >
      {({
        handleSubmit,
        errors,
        isSubmitting,
        handleBlur,
        isValid,
        setFieldValue,
        setSubmitting,
      }: FormikProps<TCertificateForm>) => (
        <Form
          placeholder=""
          onSubmit={handleSubmit}
          className={styles.certificateForm}
        >
          <StepCard title="Выберите сумму сертификата" stepNumber="1">
            <FormRadioButton
              className={styles.radioButtons}
              hasGap
              name="total"
              options={certificatesOptions}
            />
          </StepCard>
          <Spacer size={40} />
          <StepCard title="Выберите способ доставки" stepNumber="2">
            <FormRadioButton
              size="l"
              name="deliveryMethod"
              className={styles.deliveryTypeButtons}
              options={certificateDeliveryType}
            />
            <Spacer size={32} />
            <Typo variant="xl" weight="600">
              От кого
            </Typo>
            <Spacer size={8} />
            <FormTextField
              onBlur={handleBlur}
              type="email"
              label="Ваш email"
              name="fromEmail"
              invalidMessage={errors.fromEmail}
            />
            <Spacer size={32} />
            <Typo variant="xl" weight="600">
              Добавьте поздравление
            </Typo>
            <Spacer size={8} />
            <FormTextAreaField
              size={5}
              maxLength={280}
              label="Текст поздравления"
              name="message"
            />
            <RecipientFields
              emailError={errors.toEmail}
              dateError={errors.deliveryDate}
            />
          </StepCard>
          <Spacer size={40} />
          <StepCard
            title="Подарочный сертификат будет выглядеть так"
            stepNumber="3"
          >
            <CertificateCard />
          </StepCard>
          <Spacer size={40} />
          {!!errors && !!errors.recaptcha && (
            <Center>
              <Spacer size={8} />
              <Typo color="alert" variant="xm">
                {errors.recaptcha}
              </Typo>
            </Center>
          )}
          <ReCAPTCHA
            sitekey={process.env.captchaSiteKey}
            ref={recaptchaRef}
            className={styles.captcha}
            onChange={(value: string | null) => {
              setFieldValue('recaptcha', value);
              setSubmitting(false);
            }}
          />
          <Spacer size={40} />
          <Button
            variant="primary"
            size="xl"
            isDisabled={!isValid || isSubmitting}
            type="submit"
            isBlock={!isFullScreen}
          >
            Купить сертификат
          </Button>
          <Spacer size={40} />
          <Typo variant="xm" align={isFullScreen ? 'center' : 'left'}>
            Нажимая на&nbsp;кнопку &laquo;Купить сертификат&raquo;,
            вы&nbsp;принимаете условия публичной оферты.
            <br />
            Оплатите заказ онлайн с&nbsp;помощью банковской карты, другие
            способы оплаты пока недоступны.
          </Typo>
          <Spacer size={100} />
        </Form>
      )}
    </Formik>
  );
};
